 import React, { useEffect, useState } from "react";
import { 
  Card, 
  Typography, 
  Avatar, 
  Tag, 
  Space, 
  Button, 
  Row, 
  Col,
  Statistic,
  Table,
  Badge,
  Progress,
  Skeleton,
  List,
  Tooltip,
  Popover,
  Select  // <-- ADD THIS IMPORT
} from "antd";
import {
  UserOutlined,
  TeamOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
  CloseCircleOutlined,
  SendOutlined,
  SwapOutlined,
  LoginOutlined,
  LogoutOutlined,
  FileTextOutlined,
  RiseOutlined,
  ManOutlined,
  WomanOutlined,
  ReloadOutlined,
  EnvironmentOutlined,
  CalendarOutlined,
  ArrowUpOutlined,
  ArrowRightOutlined,
  FireOutlined,
  ThunderboltOutlined,
  StarOutlined,
  CrownOutlined
} from "@ant-design/icons";
import { request } from "../../../share/request";
import { getCurrentUser } from "../../../share/getProfile";
import { linkPath } from "../../../share/helper";
import moment from "moment";

const { Title, Text } = Typography;

const DashboardPage = () => {
  const getProfile = getCurrentUser();
  
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  
  const [dashboardData, setDashboardData] = useState({
    leaveToday: null,
    leaveThisYear: null,
    missions: null,
    rightManager: null,
    attendance: null,
    registrationChart: null
  });

  useEffect(() => {
    fetchAllData();
  }, []);

  const fetchAllData = async () => {
    setLoading(true);
    setRefreshing(true);
    
    try {
      const orgCode = getProfile.org_code;
      
      console.log("Fetching dashboard for org:", orgCode);
      
      const [
        leaveTodayRes,
        leaveYearRes,
        missionsRes,
        rightManagerRes,
        attendanceRes,
        chartRes
      ] = await Promise.allSettled([
        request(`dashboard/leave-today?org_code=${orgCode}`, "get", {}),
        request(`dashboard/leave-this-year?org_code=${orgCode}`, "get", {}),
        request(`dashboard/missions?org_code=${orgCode}`, "get", {}),
        request(`dashboard/right-manager?org_code=${orgCode}`, "get", {}),
        request(`dashboard/attendance?org_code=${orgCode}`, "get", {}),
        request(`dashboard/user-registration-chart?org_code=${orgCode}`, "get", {})
      ]);

      const newData = {
        leaveToday: leaveTodayRes.status === 'fulfilled' && leaveTodayRes.value?.success ? leaveTodayRes.value.data : null,
        leaveThisYear: leaveYearRes.status === 'fulfilled' && leaveYearRes.value?.success ? leaveYearRes.value.data : null,
        missions: missionsRes.status === 'fulfilled' && missionsRes.value?.success ? missionsRes.value.data : null,
        rightManager: rightManagerRes.status === 'fulfilled' && rightManagerRes.value?.success ? rightManagerRes.value.data : null,
        attendance: attendanceRes.status === 'fulfilled' && attendanceRes.value?.success ? attendanceRes.value.data : null,
        registrationChart: chartRes.status === 'fulfilled' && chartRes.value?.success ? chartRes.value.data : null
      };

      console.log("Dashboard data:", newData);
      
      setDashboardData(newData);

    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
      setTimeout(() => setRefreshing(false), 500);
    }
  };

  const { leaveToday, leaveThisYear, missions, rightManager, attendance, registrationChart } = dashboardData;

  // ================================================================
  // HELPERS
  // ================================================================
  const getNameColor = (name) => {
    const colors = ["#FF5733", "#33B5E5", "#AA66CC", "#99CC00", "#FFBB33", "#FF4444", "#0099CC", "#9933CC", "#2ecc71", "#e74c3c", "#3498db", "#9b59b6", "#1abc9c", "#e67e22", "#34495e", "#f39c12"];
    if (!name) return colors[0];
    const colorIndex = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0) % colors.length;
    return colors[colorIndex];
  };

  const getImageUrl = (image) => {
    if (!image) return null;
    if (image.startsWith('http')) return image;
    return `${linkPath}${image}`;
  };

  // ================================================================
  // AVATAR WITH DOT & BORDER
  // ================================================================
  const AvatarWithDot = ({ src, icon, name, size = 40, borderColor = '#e8e8e8', dotColor = '#52c41a' }) => (
    <div style={{ position: "relative", display: "inline-flex", flexShrink: 0 }}>
      <Avatar
        src={src ? getImageUrl(src) : undefined}
        icon={!src ? icon : undefined}
        size={size}
        style={{
          backgroundColor: !src ? getNameColor(name) : undefined,
          color: "white",
          borderRadius: 12,
          border: `3px solid ${borderColor}`,
          boxShadow: `0 0 0 2px ${borderColor}30`,
        }}
      >
        {!src && !icon ? name?.charAt(0)?.toUpperCase() : null}
      </Avatar>
      <span style={{
        position: "absolute", bottom: -2, right: -2,
        width: size > 36 ? 14 : 11, height: size > 36 ? 14 : 11,
        borderRadius: "50%", backgroundColor: dotColor,
        border: "2px solid white", boxShadow: '0 1px 3px rgba(0,0,0,0.3)'
      }} />
    </div>
  );

  // ================================================================
  // APPROVAL STEPS RENDERER
  // ================================================================
  const renderApprovalSteps = (approvalSteps, record) => {
    if (!approvalSteps || approvalSteps.length === 0) {
      return (
        <Tag
          color={record.status === "Approved" ? "success" : record.status === "Pending" ? "warning" : "error"}
          style={{ borderRadius: 20, margin: 0, fontFamily: 'Content',fontWeight:'bold' }}
          icon={record.status === "Approved" ? <CheckCircleOutlined /> : <ClockCircleOutlined />}
        >
          {record.approvd_admin || "មិនទាន់មាន"}
        </Tag>
      );
    }
    return (
      <div style={{ display: "flex", alignItems: "center", gap: 0, flexWrap: "wrap" }}>
        {approvalSteps.map((step, index) => {
          const isApproved = step.status === "Approved";
          const borderColor = isApproved ? "#52c41a" : "#faad14";
          const dotColor = isApproved ? "#52c41a" : "#faad14";
          const imageUrl = step.approver_image ? getImageUrl(step.approver_image) : null;
          return (
            <div key={index} style={{ display: "flex", alignItems: "center", gap: 0 }}>
              <Popover content={<div style={{ fontFamily: 'Content',fontWeight:'bold' }}><div><strong>{step.approver_fullname}</strong></div><div>{step.approver_position || ''}</div><Tag color={isApproved ? "success" : "warning"} style={{ fontFamily: 'Content',fontWeight:'bold', marginTop: 4 }}>{isApproved ? 'បានអនុម័ត' : 'កំពុងរង់ចាំ'}</Tag></div>}>
                <div style={{ position: "relative", cursor: "pointer" }}>
                  <div style={{ width: 34, height: 34, borderRadius: "50%", border: `2px solid ${borderColor}`, backgroundColor: isApproved ? "#f6ffed" : "#fffbe6", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden" }}>
                    {imageUrl ? <img src={imageUrl} alt="" style={{ width: "100%", height: "100%", objectFit: "cover", borderRadius: "50%" }} onError={(e) => { e.target.style.display = 'none'; }} /> : <span style={{ fontSize: 14, fontWeight: "bold", color: "#186a3b" }}>{step.approver_fullname?.charAt(0).toUpperCase() || '?'}</span>}
                  </div>
                  <div style={{ position: "absolute", bottom: -2, right: -2, width: 16, height: 16, borderRadius: "50%", backgroundColor: dotColor, display: "flex", alignItems: "center", justifyContent: "center", border: "2px solid white", boxShadow: "0 1px 3px rgba(0,0,0,0.2)" }}>
                    {isApproved ? <CheckCircleOutlined style={{ color: "white", fontSize: 10 }} /> : <ClockCircleOutlined style={{ color: "white", fontSize: 10 }} />}
                  </div>
                </div>
              </Popover>
              {index < approvalSteps.length - 1 && <div style={{ width: 14, height: 2, backgroundColor: "#d9d9d9", margin: "0 2px" }} />}
            </div>
          );
        })}
      </div>
    );
  };

  // ================================================================
  // CARDS
  // ================================================================
  const MetricCard = ({ icon, label, value, trend, color, gradient, delay = 0 }) => (
    <div style={{ background: "white", borderRadius: 20, padding: "24px", position: "relative", overflow: "hidden", boxShadow: "0 4px 20px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)", transition: "all 0.3s ease", cursor: "pointer", animation: `fadeInUp 0.6s ease-out ${delay}s both` }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 8px 30px rgba(0,0,0,0.08)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 20px rgba(0,0,0,0.04)"; }}>
      <div style={{ position: "absolute", top: -30, right: -30, width: 120, height: 120, borderRadius: "50%", background: gradient, opacity: 0.08, filter: "blur(20px)" }} />
      <div style={{ width: 52, height: 52, borderRadius: 16, background: gradient, display: "flex", alignItems: "center", justifyContent: "center", marginBottom: 16, fontSize: 22, color: "white", boxShadow: `0 4px 15px ${color}40` }}>{icon}</div>
      <Text type="secondary" style={{ fontSize: 12, fontWeight: 500, textTransform: "uppercase", letterSpacing: 1, fontFamily: 'Content',fontWeight:'bold' }}>{label}</Text>
      <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 4 }}>
        <Title level={2} style={{ margin: 0, fontWeight: 800, fontSize: 36, letterSpacing: -1 }}>{value}</Title>
        {trend && <Text style={{ color: trend > 0 ? '#4caf50' : '#f44336', fontSize: 13, fontWeight: 600 }}>{trend > 0 ? '↑' : '↓'} {Math.abs(trend)}%</Text>}
      </div>
    </div>
  );

  const GlassStatCard = ({ icon, label, value, color, bgColor }) => (
    <div style={{ background: bgColor || `${color}08`, borderRadius: 16, padding: "16px 20px", border: `1px solid ${color}15`, display: "flex", alignItems: "center", gap: 14, transition: "all 0.2s ease", cursor: "pointer" }}
      onMouseEnter={(e) => { e.currentTarget.style.background = `${color}12`; e.currentTarget.style.borderColor = `${color}30`; }}
      onMouseLeave={(e) => { e.currentTarget.style.background = bgColor || `${color}08`; e.currentTarget.style.borderColor = `${color}15`; }}>
      <div style={{ width: 44, height: 44, borderRadius: 14, background: `${color}15`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: color }}>{icon}</div>
      <div><Text type="secondary" style={{ fontSize: 11, display: "block", fontFamily: 'Content',fontWeight:'bold' }}>{label}</Text><Text strong style={{ fontSize: 20, color: "#1a1a2e" }}>{value}</Text></div>
    </div>
  );

  // ================================================================
  // LEAVE COLUMNS
  // ================================================================
  const leaveColumns = [
    {
      title: 'បុគ្គលិក', key: 'staff_name', width: 200,
      render: (_, record) => {
        const sc = record.status === 'Approved' ? '#52c41a' : record.status === 'Pending' ? '#faad14' : record.status === 'Rejected' ? '#f44336' : '#d9d9d9';
        const dn = record.staff_name || record.fullname || record.user_fullname || record.frontname || 'N/A';
        const pn = record.position_kh || record.department_name || record.department || '';
        return (
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <AvatarWithDot src={record.image} icon={<UserOutlined />} name={dn} size={40} borderColor={sc} dotColor={sc} />
            <div><span style={{ fontFamily: 'Content' }}><strong>{dn}</strong></span><br /><span style={{ fontFamily: 'Content', fontSize: 11, color: '#666' }}>{pn}</span></div>
          </div>
        );
      },
    },
    {
      title: 'អនុញ្ញាតដោយ', key: 'approval_steps', width: 160,
      render: (_, record) => renderApprovalSteps(record.approval_steps, record),
    },
    {
      title: 'ប្រភេទ', dataIndex: 'typename', key: 'typename', width: 120,
      render: (text, record) => <Tag color="orange" style={{ borderRadius: 8, padding: "2px 10px", fontFamily: 'Content',fontWeight:'bold' }}>{text || record?.type_leave || 'N/A'}</Tag>,
    },
    {
      title: 'ថ្ងៃឈប់', key: 'date', width: 100,
      render: (_, record) => <Text style={{ fontSize: 12 }}>{record.date_start ? moment(record.date_start).format('DD/MM/YYYY') : '-'}</Text>,
    },
    {
      title: 'ថ្ងៃ', dataIndex: 'leave_duration_days', key: 'leave_duration_days', align: 'center', width: 60,
      render: (days) => <span style={{ background: "#f3e5f5", color: "#9c27b0", padding: "2px 10px", borderRadius: 20, fontSize: 12, fontWeight: 600 }}>{days || 1}</span>,
    },
    {
      title: 'ស្ថានភាព', dataIndex: 'status', key: 'status', width: 140,
      render: (status, record) => {
        const c = { 'Approved': { color: '#52c41a', bg: '#f6ffed', label: 'បានអនុញ្ញាត' }, 'Pending': { color: '#faad14', bg: '#fffbe6', label: 'កំពុងរង់ចាំ' }, 'Rejected': { color: '#f44336', bg: '#fff2f0', label: 'បានបដិសេធ' } }[status] || { color: '#d9d9d9', bg: '#fafafa', label: status };
        return (
          <span style={{ background: c.bg, color: c.color, padding: "5px 14px", borderRadius: 20, fontSize: 11, fontWeight: 600, display: "inline-flex", alignItems: "center", gap: 6, border: `1.5px solid ${c.color}40`, fontFamily: 'Content',fontWeight:'bold' }}>
            <span style={{ width: 8, height: 8, borderRadius: "50%", background: c.color, boxShadow: `0 0 6px ${c.color}80` }} />{record.status_kh || c.label}
          </span>
        );
      },
    },
  ];

  // ================================================================
  // MAIN RENDER
  // ================================================================
  return (
    <div style={{ background: "linear-gradient(180deg, #f8fafc 0%, #f0f2f5 50%, #e8ecf1 100%)", minHeight: "100vh", padding: "24px 28px", fontFamily: "'Inter', 'Content', sans-serif" }}>
      <style>{`@keyframes fadeInUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }`}</style>

      {/* HEADER */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 28, flexWrap: "wrap", gap: 16 }}>
        <div>
          <Title level={2} style={{ fontFamily:'Content', margin: 0, color:'green', letterSpacing: -0.5, fontSize: 28 }}>{getProfile?.org_name_kh || 'អង្គភាព'}</Title>
          <Text type="secondary" style={{ fontSize: 13, fontFamily:'Content', fontWeight:'bold' }}>{getProfile?.province_name || ''} · {moment().format('dddd, DD MMMM YYYY')}</Text>
        </div>
        <Space>
          <Tooltip title="ធ្វើឱ្យទិន្នន័យថ្មី">
            <Button icon={<ReloadOutlined spin={refreshing} />} onClick={fetchAllData} loading={loading} size="large"
              style={{ color:'green', borderRadius: 14, background: "white", border: "1px solid #e8e8e8", fontWeight: 500, boxShadow: "0 2px 8px rgba(0,0,0,0.04)", fontFamily: 'Content',fontWeight:'bold' }}>ធ្វើឱ្យថ្មី</Button>
          </Tooltip>
        </Space>
      </div>

      {/* ROW 1: METRICS */}
      <Row gutter={[20, 20]} style={{ marginBottom: 20 }}>
        <Col xs={24} sm={12} lg={6}>{loading ? <Skeleton active paragraph={{ rows: 3 }} /> : <MetricCard icon={<FileTextOutlined />} label="សំណើឈប់សម្រាកថ្ងៃនេះ" value={leaveToday?.summary?.total_requests || 0} color="#ff9800" gradient="linear-gradient(135deg, #ff9800, #f57c00)" />}</Col>
        <Col xs={24} sm={12} lg={6}>{loading ? <Skeleton active paragraph={{ rows: 3 }} /> : <MetricCard icon={<SendOutlined />} label="បេសកកម្មកំពុងដំណើរការ" value={missions?.summary?.ongoing || 0} color="#4caf50" gradient="linear-gradient(135deg, #43e97b, #38f9d7)" />}</Col>
        <Col xs={24} sm={12} lg={6}>{loading ? <Skeleton active paragraph={{ rows: 3 }} /> : <MetricCard icon={<TeamOutlined />} label="អត្រាវត្តមាន" value={`${attendance?.statistics?.attendance_rate || 0}%`} color="#00bcd4" gradient="linear-gradient(135deg, #00bcd4, #0097a7)" />}</Col>
        <Col xs={24} sm={12} lg={6}>{loading ? <Skeleton active paragraph={{ rows: 3 }} /> : <MetricCard icon={<SwapOutlined />} label="ការផ្ទេរកំពុងដំណើរការ" value={rightManager?.summary?.active_transfers || 0} color="#9c27b0" gradient="linear-gradient(135deg, #a855f7, #6366f1)" />}</Col>
      </Row>

    {/* ROW 2: QUICK STATS + ATTENDANCE + REGISTRATIONS */}
<Row gutter={[20, 20]} style={{ marginBottom: 20 }}>
 
  {/* Attendance Summary */}
  <Col xs={24} lg={8}>
    <div style={{ background: "white", borderRadius: 20, padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)", height: "100%" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 20 }}>
        <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, #00bcd4, #0097a7)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(0,188,212,0.3)" }}><ClockCircleOutlined style={{ color: "white", fontSize: 18 }} /></div>
        <div><Text strong style={{ fontSize: 15, fontFamily: 'Content',fontWeight:'bold' }}>វត្តមានថ្ងៃនេះ</Text><Text type="secondary" style={{ fontSize: 11, fontFamily: 'Content',fontWeight:'bold', display: 'block' }}>{attendance?.statistics?.total_staff || 0} បុគ្គលិក</Text></div>
      </div>
      <div style={{ marginBottom: 16 }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6 }}><Text style={{ fontSize: 12, fontFamily: 'Content',fontWeight:'bold' }}>អត្រាវត្តមាន</Text><Text strong style={{ fontSize: 18, color: '#00bcd4' }}>{attendance?.statistics?.attendance_rate || 0}%</Text></div>
        <Progress percent={Number(attendance?.statistics?.attendance_rate || 0)} strokeColor={{ '0%': '#00bcd4', '100%': '#4caf50' }} strokeWidth={10} trailColor="#f0f0f0" showInfo={false} />
      </div>
      <Row gutter={[8, 8]}>
        <Col span={8}><div style={{ textAlign: "center" }}><LoginOutlined style={{ color: '#4caf50', fontSize: 16, marginBottom: 4 }} /><Title level={4} style={{ margin: 0, color: '#4caf50', fontWeight: 700 }}>{attendance?.statistics?.checkin_count || 0}</Title><Text style={{ fontSize: 10, color: '#999', fontFamily: 'Content',fontWeight:'bold' }}>ចូលធ្វើការ</Text></div></Col>
        <Col span={8}><div style={{ textAlign: "center" }}><CheckCircleOutlined style={{ color: '#2196f3', fontSize: 16, marginBottom: 4 }} /><Title level={4} style={{ margin: 0, color: '#2196f3', fontWeight: 700 }}>{attendance?.statistics?.staff_with_full_attendance || 0}</Title><Text style={{ fontSize: 10, color: '#999', fontFamily: 'Content',fontWeight:'bold' }}>ពេញ</Text></div></Col>
        <Col span={8}><div style={{ textAlign: "center" }}><CloseCircleOutlined style={{ color: '#f44336', fontSize: 16, marginBottom: 4 }} /><Title level={4} style={{ margin: 0, color: '#f44336', fontWeight: 700 }}>{attendance?.statistics?.staff_without_attendance || 0}</Title><Text style={{ fontSize: 10, color: '#999', fontFamily: 'Content',fontWeight:'bold'  }}>យឺត និងចេញមុន</Text></div></Col>
      </Row>
    </div>
  </Col>

  {/* Total Registrations with Bar Chart */}
  <Col xs={24} lg={8}>
    <div style={{
      background: "white", borderRadius: 20, padding: "24px",
      boxShadow: "0 4px 20px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)",
      height: "100%", display: "flex", flexDirection: "column"
    }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16, flexWrap: "wrap", gap: 8 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, #667eea, #764ba2)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(102, 126, 234, 0.3)" }}>
            <TeamOutlined style={{ color: "white", fontSize: 18 }} />
          </div>
          <div>
            <Text strong style={{ fontSize: 14, fontFamily: 'Content',fontWeight:'bold' }}>បុគ្គលិកចូលធ្វើការ</Text>
            <Text type="secondary" style={{ fontSize: 10, fontFamily: 'Content',fontWeight:'bold', display: 'block' }}>តាមខែ ({registrationChart?.year || moment().format('YYYY')})</Text>
          </div>
        </div>
        <Select
          value={registrationChart?.year}
          size="small"
          style={{ width: 85, fontFamily: 'Content',fontWeight:'bold' }}
          onChange={(value) => console.log("Selected year:", value)}
          options={(registrationChart?.available_years || []).map(y => ({ value: y, label: `${y}` }))}
        />
      </div>
      <div style={{ background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", borderRadius: 16, padding: "16px 20px", marginBottom: 16, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div>
          <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 11, fontFamily: 'Content',fontWeight:'bold' }}>សរុបទាំងអស់</Text>
          <Title level={2} style={{ color: "white", margin: 0, fontWeight: 800, fontSize: 32 }}>{registrationChart?.summary?.total_registrations || 0}</Title>
          <Text style={{ color: "rgba(255,255,255,0.7)", fontSize: 10, fontFamily: 'Content',fontWeight:'bold' }}>បុគ្គលិក</Text>
        </div>
        <div style={{ textAlign: 'right' }}>
          <div style={{ display: "flex", alignItems: "center", gap: 4, marginBottom: 4 }}><ManOutlined style={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }} /><Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, fontFamily: 'Content',fontWeight:'bold' }}>{registrationChart?.summary?.male_total || 0}</Text></div>
          <div style={{ display: "flex", alignItems: "center", gap: 4 }}><WomanOutlined style={{ color: "rgba(255,255,255,0.8)", fontSize: 14 }} /><Text style={{ color: "rgba(255,255,255,0.8)", fontSize: 13, fontFamily: 'Content',fontWeight:'bold' }}>{registrationChart?.summary?.female_total || 0}</Text></div>
        </div>
      </div>
      <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "flex-end" }}>
        {loading ? <Skeleton active paragraph={{ rows: 4 }} /> : (
          <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", gap: 2, height: 110, padding: "0 2px", position: "relative" }}>
            {(registrationChart?.monthly_data || []).map((item, idx) => {
              const maxValue = Math.max(...(registrationChart?.monthly_data || []).map(d => d.user_count), 1);
              const totalH = Math.max((item.user_count / maxValue) * 100, item.user_count > 0 ? 3 : 0);
              return (
                <Tooltip key={idx} title={<div style={{ fontFamily: 'Content',fontWeight:'bold' }}><div><strong>{item.month_name} {registrationChart?.year}</strong></div><div>សរុប: {item.user_count}នាក់</div><div>♂ ប្រុស: {item.male_count} | ♀ ស្រី: {item.female_count}</div></div>}>
                  <div style={{ flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "flex-end", height: "100%", cursor: "pointer", transition: "all 0.2s ease", position: "relative" }}
                    onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.08)"; e.currentTarget.style.zIndex = 2; }}
                    onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; e.currentTarget.style.zIndex = 1; }}>
                    <div style={{ width: "100%", height: `${totalH}%`, position: "relative", borderRadius: "3px 3px 0 0", overflow: "hidden", minHeight: item.user_count > 0 ? 3 : 0 }}>
                      {item.female_count > 0 && (
                        <div style={{ position: "absolute", bottom: 0, width: "100%", height: `${Math.max((item.female_count / maxValue) * 100, 0)}%`, background: "linear-gradient(180deg, #eb2f96 0%, #ff69b4 100%)", borderRadius: "3px 3px 0 0", minHeight: 3 }} />
                      )}
                      {item.male_count > 0 && (
                        <div style={{ position: "absolute", bottom: `${item.female_count > 0 ? Math.max((item.female_count / maxValue) * 100, 0) : 0}%`, width: "100%", height: `${Math.max((item.male_count / maxValue) * 100, 0)}%`, background: "linear-gradient(180deg, #667eea 0%, #764ba2 100%)", borderRadius: item.female_count > 0 ? 0 : "3px 3px 0 0", minHeight: 3 }} />
                      )}
                    </div>
                    <Text style={{ fontSize: 7, color: '#999', marginTop: 3, fontFamily: 'Content',fontWeight:'bold', whiteSpace: 'nowrap' }}>{item.month_name?.substring(0, 3)}</Text>
                    {item.user_count > 0 && <Text style={{ fontSize: 8, color: '#667eea', fontWeight: 600 }}>{item.user_count}</Text>}
                  </div>
                </Tooltip>
              );
            })}
          </div>
        )}
      </div>
      <div style={{ display: "flex", justifyContent: "center", gap: 12, marginTop: 10, padding: "6px 10px", background: "#fafafa", borderRadius: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: "linear-gradient(135deg, #667eea, #764ba2)" }} /><Text style={{ fontSize: 10, fontFamily: 'Content',fontWeight:'bold', color: '#666' }}>ប្រុស</Text></div>
        <div style={{ display: "flex", alignItems: "center", gap: 4 }}><span style={{ width: 10, height: 10, borderRadius: 2, background: "linear-gradient(135deg, #eb2f96, #ff69b4)" }} /><Text style={{ fontSize: 10, fontFamily: 'Content',fontWeight:'bold', color: '#666' }}>ស្រី</Text></div>
      </div>
    </div>
  </Col>

{/* Multi-Year Line Chart (2022 - 2026) with Yearly Totals */}
<Col xs={24} lg={8}>
  <div style={{
    background: "white", borderRadius: 20, padding: "24px",
    boxShadow: "0 4px 20px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)",
    height: "100%", display: "flex", flexDirection: "column"
  }}>
    {/* Header */}
    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
      <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, #11998e, #38ef7d)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(17, 153, 142, 0.3)" }}>
        <RiseOutlined style={{ color: "white", fontSize: 18 }} />
      </div>
      <div>
        <Text strong style={{ fontSize: 14, fontFamily: 'Content',fontWeight:'bold' }}>និន្នាការចូលធ្វើការ</Text>
        <Text type="secondary" style={{ fontSize: 10, fontFamily: 'Content',fontWeight:'bold', display: 'block' }}>៥ឆ្នាំចុងក្រោយ (2022 - {moment().format('YYYY')})</Text>
      </div>
    </div>

    {/* Yearly Summary Cards with Cumulative Totals */}
    <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
      {(registrationChart?.yearly_totals || []).map((yearData, yi) => {
        const colors = [
          { bg: '#667eea15', text: '#667eea', border: '#667eea30' },
          { bg: '#52c41a15', text: '#52c41a', border: '#52c41a30' },
          { bg: '#faad1415', text: '#d48806', border: '#faad1430' },
          { bg: '#f4433615', text: '#f44336', border: '#f4433630' },
          { bg: '#00bcd415', text: '#0097a7', border: '#00bcd430' }
        ];
        const c = colors[yi] || colors[0];
        const isCurrentYear = yearData.year === parseInt(moment().format('YYYY'));
        
        return (
          <Tooltip key={yi} title={<div style={{ fontFamily: 'Content',fontWeight:'bold' }}><div><strong>ឆ្នាំ {yearData.year}</strong></div><div>សរុប: {yearData.total} នាក់</div><div>♂ បុរស: {yearData.male} | ♀ នារី: {yearData.female}</div><div style={{ marginTop: 4, borderTop: '1px solid rgba(255,255,255,0.3)', paddingTop: 4 }}>ចូលថ្មី: +{yearData.new_hire} នាក់</div></div>}>
            <div style={{
              flex: "1 1 auto", minWidth: 52,
              background: isCurrentYear ? `linear-gradient(135deg, ${c.text}, ${c.text}dd)` : c.bg,
              borderRadius: 10, padding: "6px 8px 4px", textAlign: "center",
              border: `1.5px solid ${isCurrentYear ? 'transparent' : c.border}`,
              cursor: "pointer", transition: "all 0.2s ease",
              boxShadow: isCurrentYear ? `0 3px 10px ${c.text}40` : 'none',
            }}
            onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-2px)"; }}
            onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; }}>
              <Text style={{ fontSize: 8, fontFamily: 'Content',fontWeight:'bold', color: isCurrentYear ? 'rgba(255,255,255,0.7)' : '#999', display: 'block', lineHeight: 1.2 }}>{yearData.year}</Text>
              <Text strong style={{ fontSize: 16, fontFamily: 'Content',fontWeight:'bold', color: isCurrentYear ? 'white' : c.text, lineHeight: 1.1, display: 'block' }}>{yearData.total}</Text>
              <div style={{ marginTop: 3, paddingTop: 3, borderTop: `1px solid ${isCurrentYear ? 'rgba(255,255,255,0.25)' : c.border}` }}>
                <Text style={{ fontSize: 9, fontFamily: 'Content',fontWeight:'bold', color: isCurrentYear ? 'rgba(255,255,255,0.85)' : '#52c41a', fontWeight: 600 }}>+{yearData.new_hire}</Text>
              </div>
            </div>
          </Tooltip>
        );
      })}
    </div>

    {/* LINE CHART - Cumulative growth over 5 years */}
    <div style={{ flex: 1, position: "relative", minHeight: 150 }}>
      {loading ? <Skeleton active paragraph={{ rows: 5 }} /> : (
        <svg viewBox="0 0 300 150" style={{ width: "100%", height: "100%" }}>
          {/* Background gradient area */}
          <defs>
            <linearGradient id="areaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="0%" stopColor="#667eea" stopOpacity="0.3" />
              <stop offset="100%" stopColor="#667eea" stopOpacity="0.02" />
            </linearGradient>
          </defs>

          {/* Horizontal grid lines */}
          {[0, 1, 2, 3].map(i => (
            <line key={i} x1="35" y1={20 + i * 30} x2="295" y2={20 + i * 30} stroke="#f0f0f0" strokeWidth="1" />
          ))}

          {/* Y-axis labels */}
          {(() => {
            const allTotals = (registrationChart?.yearly_totals || []).map(y => y.total);
            const maxVal = Math.max(...allTotals, 1);
            const minVal = Math.min(...allTotals, 0);
            const range = maxVal - minVal || 1;
            return [0, 1, 2, 3].map(i => {
              const val = Math.round(maxVal - (i * range / 3));
              return (
                <text key={i} x="30" y={24 + i * 30} textAnchor="end" fontSize="8" fill="#aaa" fontFamily='Content'>{val}</text>
              );
            });
          })()}

          {/* Area fill under the line */}
          {(() => {
            const yearlyTotals = registrationChart?.yearly_totals || [];
            if (yearlyTotals.length < 2) return null;
            const allTotals = yearlyTotals.map(y => y.total);
            const maxVal = Math.max(...allTotals, 1);
            const minVal = Math.min(...allTotals, 0);
            const range = maxVal - minVal || 1;
            
            let areaPoints = '';
            yearlyTotals.forEach((yt, idx) => {
              const x = 42 + (idx * (248 / (yearlyTotals.length - 1)));
              const y = 110 - (((yt.total - minVal) / range) * 90);
              areaPoints += `${x},${y} `;
            });
            // Close the path to bottom
            const lastX = 42 + ((yearlyTotals.length - 1) * (248 / (yearlyTotals.length - 1)));
            areaPoints += `${lastX},110 42,110`;
            
            return <polygon points={areaPoints} fill="url(#areaGrad)" />;
          })()}

          {/* Main line */}
          {(() => {
            const yearlyTotals = registrationChart?.yearly_totals || [];
            if (yearlyTotals.length < 2) return null;
            const allTotals = yearlyTotals.map(y => y.total);
            const maxVal = Math.max(...allTotals, 1);
            const minVal = Math.min(...allTotals, 0);
            const range = maxVal - minVal || 1;
            
            let linePoints = '';
            yearlyTotals.forEach((yt, idx) => {
              const x = 42 + (idx * (248 / (yearlyTotals.length - 1)));
              const y = 110 - (((yt.total - minVal) / range) * 90);
              linePoints += `${x},${y} `;
            });
            
            return (
              <polyline
                points={linePoints.trim()}
                fill="none"
                stroke="#667eea"
                strokeWidth="3"
                strokeLinejoin="round"
                strokeLinecap="round"
              />
            );
          })()}

          {/* Data dots and labels */}
          {(() => {
            const yearlyTotals = registrationChart?.yearly_totals || [];
            if (yearlyTotals.length < 2) return null;
            const allTotals = yearlyTotals.map(y => y.total);
            const maxVal = Math.max(...allTotals, 1);
            const minVal = Math.min(...allTotals, 0);
            const range = maxVal - minVal || 1;
            
            return yearlyTotals.map((yt, idx) => {
              const x = 42 + (idx * (248 / (yearlyTotals.length - 1)));
              const y = 110 - (((yt.total - minVal) / range) * 90);
              const isCurrentYear = yt.year === parseInt(moment().format('YYYY'));
              
              return (
                <Tooltip key={idx} title={<div style={{ fontFamily: 'Content',fontWeight:'bold' }}><strong>ឆ្នាំ {yt.year}</strong><br/>សរុប: {yt.total} នាក់<br/>ថ្មី: +{yt.new_hire} នាក់</div>}>
                  <g style={{ cursor: "pointer" }}>
                    {/* Outer glow for current year */}
                    {isCurrentYear && (
                      <circle cx={x} cy={y} r="12" fill="#667eea" opacity="0.15" />
                    )}
                    {/* White circle */}
                    <circle cx={x} cy={y} r={isCurrentYear ? "7" : "5"} fill="white" stroke="#667eea" strokeWidth={isCurrentYear ? "3" : "2"} />
                    {/* Label */}
                    <text x={x} y={y - (isCurrentYear ? 14 : 10)} textAnchor="middle" fontSize={isCurrentYear ? "11" : "9"} fill="#667eea" fontFamily='Content' fontWeight="700">
                      {yt.total}
                    </text>
                  </g>
                </Tooltip>
              );
            });
          })()}

          {/* X-axis year labels */}
          {(() => {
            const yearlyTotals = registrationChart?.yearly_totals || [];
            return yearlyTotals.map((yt, idx) => {
              const x = 42 + (idx * (248 / Math.max(yearlyTotals.length - 1, 1)));
              return (
                <text key={idx} x={x} y="130" textAnchor="middle" fontSize="10" fill="#888" fontFamily='Content' fontWeight="500">
                  {yt.year}
                </text>
              );
            });
          })()}
        </svg>
      )}
    </div>

    {/* Legend */}
    <div style={{ display: "flex", justifyContent: "center", gap: 16, marginTop: 8, padding: "6px 12px", background: "#fafafa", borderRadius: 8, flexWrap: "wrap" }}>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 20, height: 3, borderRadius: 2, background: "#667eea" }} />
        <Text style={{ fontSize: 10, fontFamily: 'Content',fontWeight:'bold', color: '#666' }}>បុគ្គលិកសរុប</Text>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: "#52c41a", display: "inline-block" }} />
        <Text style={{ fontSize: 10, fontFamily: 'Content',fontWeight:'bold', color: '#666' }}>ចូលថ្មី (+{registrationChart?.yearly_totals?.[registrationChart?.yearly_totals?.length - 1]?.new_hire || 0})</Text>
      </div>
    </div>
  </div>
</Col>

</Row>
      {/* ROW 3: LEAVE REQUESTS TABLE */}
      <Row gutter={[20, 20]} style={{ marginBottom: 20 }}>
        <Col xs={24}>
          <div style={{ background: "white", borderRadius: 20, padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, #ff9800, #f57c00)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(255,152,0,0.3)" }}><FileTextOutlined style={{ color: "white", fontSize: 18 }} /></div>
                <div><Text strong style={{ fontSize: 16, fontFamily: 'Content',fontWeight:'bold' }}>សំណើឈប់សម្រាកថ្ងៃនេះ</Text><Text type="secondary" style={{ fontSize: 11, fontFamily: 'Content',fontWeight:'bold', display: 'block' }}>{leaveToday?.summary?.total_requests || 0} សំណើសរុប</Text></div>
              </div>
              <Space size={8}>
                <Tag color="success" style={{ borderRadius: 8, fontFamily: 'Content',fontWeight:'bold' }}>{leaveToday?.summary?.approved || 0} បានអនុម័ត</Tag>
                <Tag color="warning" style={{ borderRadius: 8, fontFamily: 'Content',fontWeight:'bold' }}>{leaveToday?.summary?.pending || 0} កំពុងរង់ចាំ</Tag>
                <Tag color="error" style={{ borderRadius: 8, fontFamily: 'Content',fontWeight:'bold' }}>{leaveToday?.summary?.rejected || 0} បានបដិសេធ</Tag>
              </Space>
            </div>
            <Table columns={leaveColumns} dataSource={leaveToday?.requests || []} rowKey="id"
              pagination={{ pageSize: 5, showTotal: (total) => `សរុប ${total}`, showSizeChanger: false }}
              size="middle" loading={loading} scroll={{ x: 800 }}
              locale={{ emptyText: (<div style={{ padding: 20 }}><FileTextOutlined style={{ fontSize: 40, color: '#ddd' }} /><br /><Text type="secondary" style={{ fontFamily: 'Content',fontWeight:'bold' }}>មិនមានសំណើឈប់សម្រាកថ្ងៃនេះទេ</Text></div>) }} />
          </div>
        </Col>
      </Row>

      {/* ROW 4: MISSIONS + TRANSFERS */}
      <Row gutter={[20, 20]}>
        <Col xs={24} lg={14}>
          <div style={{ background: "white", borderRadius: 20, padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20, flexWrap: "wrap", gap: 12 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, #43e97b, #38f9d7)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(67,233,123,0.3)" }}><SendOutlined style={{ color: "white", fontSize: 18 }} /></div>
                <div>
                  <Text strong style={{ fontSize: 16, fontFamily: 'Content',fontWeight:'bold' }}>បេសកកម្ម</Text>
                  {missions?.staff_on_mission && missions.staff_on_mission.length > 0 && (
                    <Text type="secondary" style={{ fontSize: 11, fontFamily: 'Content',fontWeight:'bold', display: 'block' }}>👥 {missions.staff_on_mission.length}នាក់កំពុងបំពេញបេសកកម្មថ្ងៃនេះ</Text>
                  )}
                </div>
              </div>
              <Space size={6} wrap>
                {[{ label: "ដំណើរការ", value: missions?.summary?.ongoing || 0, color: "#52c41a" },{ label: "ខាងមុខ", value: missions?.summary?.upcoming || 0, color: "#2196f3" },{ label: "បញ្ចប់", value: missions?.summary?.completed || 0, color: "#d9d9d9" },{ label: "លុបចោល", value: missions?.summary?.cancelled || 0, color: "#f44336" }].map((item, idx) => (
                  <span key={idx} style={{ background: `${item.color}15`, color: item.color, padding: "3px 10px", borderRadius: 8, fontSize: 11, fontWeight: 600, fontFamily: 'Content',fontWeight:'bold', border: `1px solid ${item.color}30` }}>{item.label} {item.value}</span>
                ))}
              </Space>
            </div>
            {missions?.staff_on_mission && missions.staff_on_mission.length > 0 && (
              <div style={{ marginBottom: 16, padding: "12px 16px", background: "#f6ffed", borderRadius: 12, border: "1px solid #b7eb8f", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
                <Text strong style={{ fontFamily: 'Content',fontWeight:'bold', fontSize: 12, color: '#52c41a', whiteSpace: 'nowrap' }}>បុគ្គលិកកំពុងបេសកកម្ម:</Text>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                  {missions.staff_on_mission.map((staff, idx) => (
                    <Tooltip key={idx} title={`${staff.fullname}${staff.position_kh ? ' | ' + staff.position_kh : ''}`}>
                      <div><AvatarWithDot src={staff.image} icon={<UserOutlined />} name={staff.fullname} size={36} borderColor="#52c41a" dotColor="#52c41a" /></div>
                    </Tooltip>
                  ))}
                </div>
              </div>
            )}
            {loading ? <Skeleton active paragraph={{ rows: 4 }} /> : (
              <List dataSource={missions?.all?.slice(0, 5) || []} split={true}
                renderItem={(item) => {
                  const today = moment().format('YYYY-MM-DD');
                  const isActive = item.status === 1 && item.joindate_start <= today && item.joindate_end >= today;
                  const isUpcoming = item.status === 1 && item.joindate_start > today;
                  const isCancelled = item.status === 2;
                  const statusColor = isActive ? '#52c41a' : isUpcoming ? '#2196f3' : isCancelled ? '#f44336' : '#d9d9d9';
                  let staffInfo = [];
                  try { staffInfo = typeof item.staff_info === 'string' ? JSON.parse(item.staff_info) : (item.staff_info || []); } catch (e) { staffInfo = []; }
                  return (
                    <div style={{ padding: "14px 0" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 10 }}>
                        <div style={{ flex: 1, minWidth: 200 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <span style={{ background: `${statusColor}15`, color: statusColor, padding: "3px 12px", borderRadius: 20, fontSize: 11, fontWeight: 600, fontFamily: 'Content',fontWeight:'bold', border: `1.5px solid ${statusColor}40`, display: "inline-flex", alignItems: "center", gap: 5 }}>
                              <span style={{ width: 7, height: 7, borderRadius: "50%", background: statusColor, boxShadow: `0 0 6px ${statusColor}80` }} />{item.status_kh}
                            </span>
                            <Text strong style={{ fontFamily: 'Content',fontWeight:'bold', fontSize: 14 }}>{item.transfer_name || item.letters_number}</Text>
                          </div>
                          <div style={{ marginTop: 6 }}><Text type="secondary" style={{ fontSize: 11, fontFamily: 'Content',fontWeight:'bold' }}><EnvironmentOutlined style={{ marginRight: 4 }} />{item.location || 'N/A'}</Text></div>
                          <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                            <Text style={{ fontSize: 11 }}>{item.joindate_start ? moment(item.joindate_start).format('DD/MM/YYYY') : '-'} → {item.joindate_end ? moment(item.joindate_end).format('DD/MM/YYYY') : '-'}</Text>
                            <Tag color="cyan" style={{ fontSize: 10, fontFamily: 'Content',fontWeight:'bold' }}>{item.mission_duration_days || 0}ថ្ងៃ</Tag>
                          </div>
                          {staffInfo.length > 0 && (
                            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 8 }}>
                              {staffInfo.map((staff, idx) => {
                                const isGov = staff.stafftype === 'រដ្ឋ';
                                return (
                                  <Tooltip key={idx} title={`${staff.fullname}${staff.stafftype ? ' - ' + staff.stafftype : ''}`}>
                                    <div><AvatarWithDot src={staff.image} icon={<UserOutlined />} name={staff.fullname} size={32} borderColor={isGov ? '#52c41a' : '#fadb14'} dotColor={isGov ? '#52c41a' : '#fadb14'} /></div>
                                  </Tooltip>
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div style={{ textAlign: 'right', flexShrink: 0 }}>
                          <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>{item.letters_type}</Text>
                          {item.prepare_by && <Text type="secondary" style={{ fontSize: 10, display: 'block' }}>រៀបចំ: {item.prepare_by}</Text>}
                        </div>
                      </div>
                    </div>
                  );
                }}
                locale={{ emptyText: (<div style={{ padding: 20, textAlign: "center" }}><SendOutlined style={{ fontSize: 40, color: '#ddd' }} /><br /><Text type="secondary" style={{ fontFamily: 'Content',fontWeight:'bold' }}>រកមិនឃើញបេសកកម្មទេ</Text></div>) }}
              />
            )}
          </div>
        </Col>
        <Col xs={24} lg={10}>
          <div style={{ background: "white", borderRadius: 20, padding: "24px", boxShadow: "0 4px 20px rgba(0,0,0,0.04)", border: "1px solid rgba(0,0,0,0.04)" }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <div style={{ width: 40, height: 40, borderRadius: 12, background: "linear-gradient(135deg, #a855f7, #6366f1)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 4px 12px rgba(168,85,247,0.3)" }}><SwapOutlined style={{ color: "white", fontSize: 18 }} /></div>
                <div><Text strong style={{ fontSize: 16, fontFamily: 'Content',fontWeight:'bold' }}>ការផ្ទេរសិទ្ធិ</Text><Text type="secondary" style={{ fontSize: 11, fontFamily: 'Content',fontWeight:'bold', display: 'block' }}>{rightManager?.summary?.active_transfers || 0} កំពុងដំណើរការ</Text></div>
              </div>
            </div>
            <List dataSource={rightManager?.transfers?.slice(0, 5) || []} split={false} loading={loading}
              renderItem={(item) => {
                const isActive = item.date_end >= moment().format('YYYY-MM-DD');
                return (
                  <div style={{ padding: "14px 16px", borderRadius: 14, marginBottom: 8, background: isActive ? '#faf5ff' : '#fafafa', border: `1px solid ${isActive ? '#e9d5ff' : '#f0f0f0'}`, transition: "all 0.2s ease", cursor: "pointer" }}
                    onMouseEnter={(e) => { e.currentTarget.style.background = isActive ? '#f3e8ff' : '#f5f5f5'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.background = isActive ? '#faf5ff' : '#fafafa'; }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                      <AvatarWithDot src={item.transfer_in_image} icon={<UserOutlined />} name={item.transfer_in_fullname} size={40} borderColor="#faad14" dotColor="#faad14" />
                      <div style={{ flex: 1, minWidth: 0 }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 4, flexWrap: "wrap" }}>
                          <Tag color="warning" style={{ fontSize: '11px', fontFamily: 'Content',fontWeight:'bold' }}><strong>{item.transfer_in_frontname} {item.transfer_in_fullname}</strong></Tag>
                          <ArrowRightOutlined style={{ color: isActive ? '#a855f7' : '#9e9e9e', fontSize: 12 }} />
                          <Tag color="success" style={{ fontSize: '11px', fontFamily: 'Content',fontWeight:'bold' }}><strong>{item.transfer_out_frontname} {item.transfer_out_fullname}</strong></Tag>
                        </div>
                        <div style={{ marginTop: 6 }}>
                          <Space size={4} wrap>
                            <Tag color="purple" style={{ borderRadius: 6, fontSize: 10, fontFamily: 'Content',fontWeight:'bold' }}>{item.transfer_type_kh}</Tag>
                            <Text type="secondary" style={{ fontSize: 10 }}>{item.date_start ? moment(item.date_start).format('DD/MM') : '-'} - {item.date_end ? moment(item.date_end).format('DD/MM') : '-'}</Text>
                          </Space>
                        </div>
                      </div>
                      <AvatarWithDot src={item.transfer_out_image} icon={<UserOutlined />} name={item.transfer_out_fullname} size={40} borderColor="#52c41a" dotColor="#52c41a" />
                    </div>
                  </div>
                );
              }}
              locale={{ emptyText: (<div style={{ padding: 20, textAlign: "center" }}><SwapOutlined style={{ fontSize: 40, color: '#ddd' }} /><br /><Text type="secondary" style={{ fontFamily: 'Content',fontWeight:'bold' }}>មិនមានការផ្ទេរទេ</Text></div>) }}
            />
          </div>
        </Col>
      </Row>
    </div>
  );
};

export default DashboardPage;